/**
 * What the pin control above a rail bubble should do, and what its tooltip says.
 *
 * Pure, and in its own module rather than inside ComparisonPage, for the same reason as
 * pairSelection.ts: the page pulls in ESM modules jest cannot parse.
 *
 * The rules, from docs/specs/policy-version-pinning.md §6.1:
 *
 * - A pinned version always shows its marker, selected or not. The marker doubles as the
 *   unpin button, so it is the one control that is reachable without selecting first.
 * - An unselected, unpinned version shows nothing. The rail must not sprout a control on
 *   every bubble; the affordance follows the selection.
 * - A selected, unpinned version offers to pin, unless its rubric is blank (nothing can
 *   be marked against an empty rubric) or an admin has ruled its labels different from the
 *   current rubric (Stylus should not mark with a version that verdict set aside). The
 *   server refuses both, so this only explains the refusal up front.
 *
 * The check is at pin time only. A pinned version ruled different LATER keeps its pin and
 * the page warns -- see `pinWarning`.
 */
import { PolicyVersionState } from '../../../types';

export type PinAffordance =
  | { kind: 'none' }
  | { kind: 'pin'; tooltip: string }
  | { kind: 'unpin'; tooltip: string }
  | { kind: 'disabled'; tooltip: string };

export const pinAffordance = (version: PolicyVersionState, selected: boolean): PinAffordance => {
  if (version.isPinned) {
    return {
      kind: 'unpin',
      tooltip: `Pinned: Stylus marks with v${version.version}. Click to unpin, so Stylus follows the current version again.`,
    };
  }
  if (!selected) return { kind: 'none' };
  if (version.status === 'unusable') {
    return { kind: 'disabled', tooltip: 'Blank rubric text cannot be pinned.' };
  }
  // A recorded "different" verdict. Stylus must not be pointed at a rubric an admin has
  // already set aside. Unjudged and no-labels versions are NOT blocked: nobody has ruled
  // against them. The server enforces the same two statuses.
  if (version.status === 'excluded') {
    return {
      kind: 'disabled',
      tooltip: `Labels on v${version.version} were ruled different from the current rubric, so it cannot be pinned. Pin another version, or reverse that verdict first.`,
    };
  }
  return {
    kind: 'pin',
    tooltip: `Pin v${version.version}: Stylus marks with this version until unpinned.`,
  };
};

/**
 * The warning to show when the pinned version is no longer one eval ground truth counts.
 *
 * Eligibility is anchored to the current version (ADR-0001); a pin points Stylus at
 * another. That tension exists for ANY pinned non-current version, but it is only worth
 * an alert once an admin has actually ruled the pinned version's labels different, or
 * the rubric is blank -- the same two statuses that block pinning in the first place. The
 * verdict itself is never blocked: a false equivalence or an unrelated unpin would be
 * worse than a visible mismatch.
 */
export const pinWarning = (
  versions: PolicyVersionState[]
): { pinned: PolicyVersionState; current: PolicyVersionState | undefined } | null => {
  const pinned = versions.find(version => version.isPinned);
  if (!pinned) return null;
  if (pinned.status !== 'excluded' && pinned.status !== 'unusable') return null;
  return { pinned, current: versions.find(version => version.isCurrent) };
};

/**
 * The snackbar line after a pin lands. Names the version it displaced, if any, because
 * "one pin per criterion" means pinning is also un-pinning and that should be said.
 */
export const pinnedMessage = (version: number, previous: number | null): string =>
  previous === null || previous === version
    ? `Pinned v${version}`
    : `Pinned v${version} (was v${previous})`;
